import {
  POWERUP_BOMB_MAX,
  POWERUP_LIFE_MAX,
  POWERUP_OVERDRIVE_SEC,
  POWERUP_SURGE_FILL,
} from '../constants';
import { livesCap } from '../constraints';
import { Ev } from '../events';
import { FEEL } from '../FeelConstants';
import { PickupKind } from '../pools/PickupPool';
import type { World } from '../World';

/**
 * Applique l'effet INSTANTANÉ d'un power-up ramassé. La monnaie `Cell` ne passe
 * pas par ici (son économie arrive au J7) : seuls les jetons de bonus agissent.
 *
 * Tous les effets sont bornés — un stock qui déborde est simplement gaspillé,
 * jamais une valeur qui file. C'est ce qui garde le power-up « nerveux mais pas
 * cassé » : on court le chercher, mais il ne remplace pas la progression par
 * cartes (§6.7).
 */
function applyPickup(w: World, kind: number): void {
  const p = w.player;
  switch (kind) {
    case PickupKind.Bomb:
      if (p.bombs < POWERUP_BOMB_MAX) p.bombs++;
      break;
    case PickupKind.Repair: {
      // Plafond : la contrainte volontaire prime (elle peut interdire les vies) ;
      // à défaut, un plafond de confort pour que la réparation reste un cadeau rare.
      const lc = livesCap(w.run.constraints);
      const cap = lc > 0 ? lc : POWERUP_LIFE_MAX;
      if (p.lives < cap) p.lives++;
      break;
    }
    case PickupKind.Surge:
      p.ulti = Math.min(1, p.ulti + POWERUP_SURGE_FILL);
      break;
    case PickupKind.Coolant:
      // Vidange : le canon redevient froid sur-le-champ, surchauffe annulée.
      p.heat = 0;
      p.overheated = false;
      break;
    case PickupKind.Overdrive:
      // Suralimentation : la fenêtre repart de zéro à chaque jeton (pas de cumul
      // au-delà de sa durée pleine — on prolonge, on n'empile pas).
      p.overdriveT = POWERUP_OVERDRIVE_SEC;
      break;
    default:
      break;
  }
}

/**
 * Ramassage des objets.
 *
 * Ce système manquait au J1, et son absence était un vrai bug — pas une
 * fonctionnalité reportée. Les ramassables n'avaient qu'un seul chemin de
 * libération : sortir par le bas de l'écran. Or l'aimantation les capture bien
 * avant, et sans dissipation ils orbitaient indéfiniment autour du joueur. Le
 * pool saturait à 128 en quelques minutes, puis refusait tout nouveau butin.
 *
 * Le symptôme était invisible : les ramassables ne sont pas encore rendus, et
 * la seule trace était un compteur de saturation dans le HUD. C'est exactement
 * la forme de panne qu'une session de test courte ne révèle jamais.
 *
 * Itération EN ARRIÈRE : la boucle libère.
 */
export function pickupSystem(w: World): void {
  const pu = w.pickups;
  const p = w.player;
  const grab2 = FEEL.pickupGrabRadius * FEEL.pickupGrabRadius;

  for (let i = pu.count - 1; i >= 0; i--) {
    const s = pu.at(i);

    // Filet de sécurité indépendant de la trajectoire : quoi qu'il arrive à un
    // ramassable — orbite stable, coincé hors champ, joueur mort au mauvais
    // moment — son slot revient au pool. Une capacité fixe sans garantie de
    // libération est une fuite qui attend son scénario.
    const age = pu.age[s]!;
    if (age > FEEL.pickupTtl) {
      pu.free(s);
      continue;
    }

    if (p.dead) continue;

    const dx = p.x - pu.x[s]!;
    const dy = p.y - pu.y[s]!;
    if (dx * dx + dy * dy < grab2) {
      // L'EFFET d'abord (bombe, vie, ulti, refroidissement, suralimentation),
      // puis l'événement qui porte le TYPE (`a`) pour que présentation et audio
      // choisissent le bon son/éclat. La monnaie `Cell` n'a pas d'effet : son
      // économie arrive au J7.
      applyPickup(w, pu.kind[s]!);
      w.events.push(Ev.PickupCollected, pu.kind[s]!, pu.x[s]!, pu.y[s]!, 1);
      pu.free(s);
    }
  }
}
